import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { UserPlus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    shop_name: "",
    phone: "",
    logo_url: "",
    business_hours: { start: 9, end: 21, days: [1, 2, 3, 4, 5, 6] },
    services: [{ name: "Corte Clásico", price: 500, duration: 30 }]
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleHoursChange = (e) => {
    setFormData({
      ...formData,
      business_hours: { ...formData.business_hours, [e.target.name]: parseInt(e.target.value) }
    });
  };

  const handleServiceChange = (index, field, value) => {
    const newServices = [...formData.services];
    newServices[index][field] = field === 'price' ? parseInt(value) || 0 : value;
    setFormData({ ...formData, services: newServices });
  };

  const addService = () => {
    setFormData({
      ...formData,
      services: [...formData.services, { name: "", price: 0, duration: 30 }]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/register`, formData);
      toast.success("¡Registro exitoso! Revisa tu correo.");
      
      // 🚀 AQUÍ ESTÁ LA MAGIA NUEVA: 
      // En lugar de mostrar una tarjeta, saltamos directo a la pantalla de verificación
      // y nos llevamos el email en la "mochila" (state).
      navigate("/admin/verify-email", { state: { email: formData.email } });
      
    } catch (error) {
      const message = error.response?.data?.detail || "Error al registrarse";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <img src="/logo192.png" alt="Barbershop Logo" className="w-16 h-16 object-contain rounded-xl" />
          </Link>
          <h1 className="font-heading text-3xl font-bold uppercase text-white mb-2">
            Registro Admin
          </h1>
          <p className="text-zinc-400">Crea tu cuenta de administrador</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 bg-zinc-900 border border-zinc-800 p-8 rounded-xl shadow-xl">
          <div>
            <Label className="text-zinc-400 text-sm uppercase tracking-wider font-heading mb-2 block">
              Nombre Completo
            </Label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Juan Pérez"
              className="input-field h-12 bg-zinc-800 border-zinc-700 text-white"
              required
              data-testid="register-name"
            />
          </div>

          <div>
            <Label className="text-zinc-400 text-sm uppercase tracking-wider font-heading mb-2 block">
              Nombre de tu Barbershop
            </Label>
            <Input
              type="text"
              name="shop_name"
              value={formData.shop_name}
              onChange={handleChange}
              placeholder="Mi Barbería"
              className="input-field h-12 bg-zinc-800 border-zinc-700 text-white"
              required
              data-testid="register-shop"
            />
          </div>

          <div>
            <Label className="text-zinc-400 text-sm uppercase tracking-wider font-heading mb-2 block">
              Email
            </Label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="tu@email.com"
              className="input-field h-12 bg-zinc-800 border-zinc-700 text-white"
              required
              data-testid="register-email"
            />
          </div>

          <div>
            <Label className="text-zinc-400 text-sm uppercase tracking-wider font-heading mb-2 block">
              Teléfono
            </Label>
            <Input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="809-555-0000"
              className="input-field h-12 bg-zinc-800 border-zinc-700 text-white"
              data-testid="register-phone"
            />
          </div>

          <div>
            <Label className="text-zinc-400 text-sm uppercase tracking-wider font-heading mb-2 block">
              Contraseña
            </Label>
            <Input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              className="input-field h-12 bg-zinc-800 border-zinc-700 text-white"
              required
              minLength={6}
              data-testid="register-password"
            />
          </div>

          {/* --- SECCIÓN: LOGO --- */}
          <div className="pt-4 border-t border-zinc-800">
            <Label className="text-zinc-400 text-sm uppercase tracking-wider font-heading mb-2 block">
              URL de tu Logo (Opcional)
            </Label>
            <Input
              type="text"
              name="logo_url"
              value={formData.logo_url}
              onChange={handleChange}
              placeholder="Ej: https://misitio.com/logo.png"
              className="input-field h-12 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>

          {/* --- SECCIÓN: HORARIOS --- */}
          <div>
            <Label className="text-zinc-400 text-sm uppercase tracking-wider font-heading mb-2 block">
              Horario de Atención (24h)
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-500 text-xs mb-1 block">Apertura</Label>
                <Input
                  type="number"
                  name="start"
                  value={formData.business_hours.start}
                  onChange={handleHoursChange}
                  min="0"
                  max="23"
                  className="input-field h-12 bg-zinc-800 border-zinc-700 text-white text-center font-bold"
                />
              </div>
              <div>
                <Label className="text-zinc-500 text-xs mb-1 block">Cierre</Label>
                <Input
                  type="number"
                  name="end"
                  value={formData.business_hours.end}
                  onChange={handleHoursChange}
                  min="0"
                  max="23"
                  className="input-field h-12 bg-zinc-800 border-zinc-700 text-white text-center font-bold"
                />
              </div>
            </div>
          </div>

          {/* --- SECCIÓN: SERVICIOS --- */}
          <div className="pb-4 border-b border-zinc-800">
            <Label className="text-zinc-400 text-sm uppercase tracking-wider font-heading mb-4 block">
              Tus Servicios y Precios
            </Label>
            {formData.services.map((service, index) => (
              <div key={index} className="flex gap-2 mb-3">
                <Input
                  type="text"
                  placeholder="Ej: Corte + Barba"
                  value={service.name}
                  onChange={(e) => handleServiceChange(index, 'name', e.target.value)}
                  className="input-field h-12 flex-1 bg-zinc-800 border-zinc-700 text-white"
                />
                <Input
                  type="number"
                  placeholder="RD$"
                  value={service.price}
                  onChange={(e) => handleServiceChange(index, 'price', e.target.value)}
                  className="input-field h-12 w-28 bg-zinc-800 border-zinc-700 text-white text-center font-bold text-amber-500"
                />
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={addService}
              className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white mt-2 h-10"
            >
              + Agregar otro servicio
            </Button>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-amber-500 text-black hover:bg-amber-400 font-heading uppercase tracking-wider font-bold h-12"
            data-testid="register-btn"
          >
            {loading ? "Registrando..." : "Crear Cuenta"} 
            {!loading && <UserPlus className="w-5 h-5 ml-2" />}
          </Button>

          <p className="text-zinc-500 text-xs text-center mt-4">
            Al registrarte, te enviaremos un código para verificar tu correo.
          </p>
        </form>

        <p className="text-center mt-6 text-zinc-500">
          ¿Ya tienes cuenta?{" "}
          <Link to="/admin/login" className="text-amber-500 hover:text-amber-400 font-bold">
            Inicia sesión
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AdminRegister;